// src/components/dashboard/tabs/CreateTutorDialog.js
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    AlertTitle,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@mui/material';
import GradientButton from '../../ui/GradientButton';
import {Activity, Book, Bot, Check} from 'lucide-react';
import {repositoriesApi, tutorsApi} from '../../../services/api';
import {useError} from '../../../contexts/ErrorContext';
import FormDialog from '../../common/FormDialog';

const CreateTutorDialog = ({ open, onClose, onSubmit, token }) => {
    const { t } = useTranslation();
    const { showError } = useError();

    // Состояния формы
    const [formData, setFormData] = useState({
        name: '',
        instructions: '',
        instruction_type: 'assistant',
        repository: ''
    });

    // Состояния UI
    const [loading, setLoading] = useState(false);
    const [repositories, setRepositories] = useState([]);
    const [loadingRepositories, setLoadingRepositories] = useState(false);
    const [formError, setFormError] = useState('');

    // Загрузка списка репозиториев
    useEffect(() => {
        if (open) {
            loadRepositories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, token]);

    const loadRepositories = async () => {
        setLoadingRepositories(true);
        try {
            const response = await repositoriesApi.getMaterialRepositories(token);
            setRepositories(response || []);
        } catch (err) {
            showError(t('dashboard.ai_tutors.error.loading_repositories'));
        } finally {
            setLoadingRepositories(false);
        }
    };

    // Обработчик изменения полей формы
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Валидация формы
    const validateForm = () => {
        if (!formData.name.trim()) {
            setFormError(t('dashboard.ai_tutors.error.name_required'));
            return false;
        }
        if (!formData.instructions.trim()) {
            setFormError(t('dashboard.ai_tutors.error.instructions_required'));
            return false;
        }
        return true;
    };

    // Обработчик отправки формы
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setFormError('');

        try {
            // Подготовка данных для отправки
            const tutorData = {
                ...formData
            };

            // Создаем нового тьютора
            const newTutor = await tutorsApi.createAITutor(tutorData, token);

            // Если выбран репозиторий, привязываем его к тьютору
            if (formData.repository) {
                try {
                    await repositoriesApi.linkRepositoryToTutor(newTutor.id, formData.repository, token);
                } catch (linkError) {
                    // Не делаем фатальную ошибку, если линковка не удалась
                }
            }

            // Вызываем колбэк с новым тьютором
            onSubmit(newTutor);

            // Закрываем диалог
            handleClose();
        } catch (err) {
            setFormError(t('dashboard.ai_tutors.error.creating'));
            showError(t('dashboard.ai_tutors.error.creating'));
        } finally {
            setLoading(false);
        }
    };

    // Обработчик закрытия диалога
    const handleClose = () => {
        if (!loading) {
            setFormData({
                name: '',
                instructions: '',
                instruction_type: 'assistant',
                repository: ''
            });
            setFormError('');
            onClose();
        }
    };

    // Содержимое диалога
    const dialogContent = (
        <>
            {formError && (
                <Alert severity="error" className="mb-4">
                    <AlertTitle>{t('dashboard.ai_tutors.error.title')}</AlertTitle>
                    {formError}
                </Alert>
            )}

            <div className="space-y-4 mt-2">
                {/* Имя тьютора */}
                <TextField
                    name="name"
                    label={t('dashboard.ai_tutors.create_dialog.name')}
                    value={formData.name}
                    onChange={handleInputChange}
                    fullWidth
                    required
                />

                {/* Тип тьютора */}
                <FormControl component="fieldset" className="mb-4">
                    <FormLabel component="legend">
                        {t('dashboard.ai_tutors.create_dialog.type')}
                    </FormLabel>
                    <RadioGroup
                        name="instruction_type"
                        value={formData.instruction_type}
                        onChange={handleInputChange}
                        row
                    >
                        <FormControlLabel
                            value="assistant"
                            control={<Radio />}
                            label={
                                <div className="flex items-center gap-2">
                                    <Book className="text-blue-500 w-4 h-4" />
                                    {t('dashboard.ai_tutors.type.assistant')}
                                </div>
                            }
                        />
                        <FormControlLabel
                            value="feedback"
                            control={<Radio />}
                            label={
                                <div className="flex items-center gap-2">
                                    <Activity className="text-green-500 w-4 h-4" />
                                    {t('dashboard.ai_tutors.type.feedback')}
                                </div>
                            }
                        />
                    </RadioGroup>
                </FormControl>

                {/* Инструкции */}
                <TextField
                    name="instructions"
                    label={
                        formData.instruction_type === 'feedback'
                            ? t('dashboard.ai_tutors.create_dialog.feedback_instructions')
                            : t('dashboard.ai_tutors.create_dialog.assistant_instructions')
                    }
                    value={formData.instructions}
                    onChange={handleInputChange}
                    multiline
                    rows={6}
                    fullWidth
                    required
                    placeholder={
                        formData.instruction_type === 'feedback'
                            ? t('dashboard.ai_tutors.create_dialog.feedback_placeholder')
                            : t('dashboard.ai_tutors.create_dialog.assistant_placeholder')
                    }
                />

                {/* Выбор репозитория */}
                <div className="mt-4">
                    <FormControl fullWidth>
                        <FormLabel component="legend" className="mb-2">
                            {t('dashboard.ai_tutors.create_dialog.repository')}
                        </FormLabel>
                        {loadingRepositories ? (
                            <div className="flex items-center gap-2 text-gray-500">
                                <CircularProgress size={16} />
                                <Typography variant="body2">
                                    {t('dashboard.ai_tutors.create_dialog.loading_repositories')}
                                </Typography>
                            </div>
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel id="repository-select-label">
                                    {t('dashboard.ai_tutors.create_dialog.repository')}
                                </InputLabel>
                                <Select
                                    labelId="repository-select-label"
                                    name="repository"
                                    value={formData.repository || ''}
                                    onChange={handleInputChange}
                                    label={t('dashboard.ai_tutors.create_dialog.repository')}
                                >
                                    <MenuItem value="">
                                        {t('dashboard.ai_tutors.create_dialog.no_repository')}
                                    </MenuItem>
                                    {repositories.map((repo) => (
                                        <MenuItem key={repo.id} value={repo.id}>
                                            {repo.name} ({repo.materials_count || 0} {t('dashboard.ai_tutors.details_dialog.materials')})
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {t('dashboard.ai_tutors.create_dialog.repository_helper')}
                                </FormHelperText>
                            </FormControl>
                        )}
                    </FormControl>
                </div>

                <Typography variant="body2" color="textSecondary" className="mt-4">
                    {t('dashboard.ai_tutors.create_dialog.note')}
                </Typography>
            </div>
        </>
    );

    // Иконка для заголовка
    const titleIcon = <Bot className="text-purple-500 mr-2" />;

    // Используем кастомный компонент для кнопки отправки
    const customSubmitButton = loading ? (
        <GradientButton
            disabled={true}
            startIcon={<CircularProgress size={20} />}
        >
            {t('dashboard.ai_tutors.create_dialog.creating')}
        </GradientButton>
    ) : (
        <GradientButton
            startIcon={<Check />}
        >
            {t('dashboard.ai_tutors.create_dialog.create')}
        </GradientButton>
    );

    return (
        <FormDialog
            open={open}
            onClose={handleClose}
            title={
                <div className="flex items-center">
                    {titleIcon}
                    {t('dashboard.ai_tutors.create_dialog.title')}
                </div>
            }
            submitLabel={null} // Используем кастомную кнопку
            cancelLabel={t('dashboard.ai_tutors.create_dialog.cancel')}
            onSubmit={handleSubmit}
            isSubmitting={loading}
            isValid={formData.name.trim() !== '' && formData.instructions.trim() !== ''}
            maxWidth="md"
            customSubmitButton={customSubmitButton}
        >
            {dialogContent}
        </FormDialog>
    );
};

export default CreateTutorDialog;
